// SPATIAL AUDIO.
//
// The shop's WebAudio layer: a four-speaker rig (two mains at the front wall,
// two fills further back), a looping ambience bed, a synthesised convolution
// reverb, and the demo player that streams a track through an HTMLAudioElement
// into the rig.
//
// Signal flow, once createShopAudio has built it:
//
//   element -> mediaSource -> programGain -> splitter
//     splitter[ch] -> per-speaker lowpass -> trim -> HRTF panner -> masterGain
//                                                 \-> send -> reverbSend
//   ambience src -> gain -> [eq] -> HRTF panner -> ambienceGain -> masterGain
//                                              \-> send -> reverbSend
//   reverbSend -> convolver -> reverbWet -> masterGain
//   masterGain -> compressor -> destination
//
// update() re-points the AudioListener from the camera's world matrix each
// frame and drives two distance-following controls off it: a lowpass on every
// speaker feed, and the reverb wet level. It also enforces the demo length
// limit.
//
// If there is no AudioContext, or the context cannot be constructed, or it has
// no createPanner, createShopAudio returns a no-op object with the same shape.
//
// Recovered from the production bundle. Numbers and strings are byte-identical
// to the shipped source.

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const SPEAKERS = [{
    id: "main-l",
    role: "main",
    channel: 0,
    x: -2.6,
    y: 2.34,
    z: 5.95,
    aim: {
        x: .6,
        y: 1.5,
        z: 0
    },
    gain: 1,
    refDistance: 2.6,
    rolloff: .85
}, {
    id: "main-r",
    role: "main",
    channel: 1,
    x: 2.6,
    y: 2.34,
    z: 5.95,
    aim: {
        x: -.6,
        y: 1.5,
        z: 0
    },
    gain: 1,
    refDistance: 2.6,
    rolloff: .85
}, {
    id: "fill-l",
    role: "fill",
    channel: 0,
    x: -2.6,
    y: 2.34,
    z: -3.6,
    aim: {
        x: .6,
        y: 1.5,
        z: -3.6
    },
    gain: .34,
    refDistance: 1.6,
    rolloff: 1.5
}, {
    id: "fill-r",
    role: "fill",
    channel: 1,
    x: 2.6,
    y: 2.34,
    z: -3.6,
    aim: {
        x: -.6,
        y: 1.5,
        z: -3.6
    },
    gain: .34,
    refDistance: 1.6,
    rolloff: 1.5
}];

const AMBIENCE_BEDS = [{
    id: "room-tone",
    url: "/ambience/room-murmur.mp3",
    x: 0,
    y: 2.1,
    z: 1.2,
    gain: .045,
    refDistance: 6,
    rolloff: .6,
    rate: .92,
    eq: {
        type: "lowpass",
        freq: 700,
        q: .5
    }
}];

const DEMO_SECONDS = 30;

// Builds and returns a new stereo impulse-response buffer: sparse noise bursts
// through a one-pole lowpass, under a power curve decay envelope, with a short
// ramp at the head that thins the spark density.
function makeReverbImpulse(context, seconds = .55, decayPower = 3.2, damping = 2.4) {
    const sampleRate = context.sampleRate,
        frameCount = Math.max(1, Math.floor(sampleRate * seconds)),
        buffer = context.createBuffer(2, frameCount, sampleRate),
        rampFrames = Math.max(1, Math.floor(sampleRate * .06));
    for (let channel = 0; channel < 2; channel += 1) {
        const data = buffer.getChannelData(channel);
        let filterState = 0;
        for (let frame = 0; frame < frameCount; frame += 1) {
            const position = frame / frameCount,
                envelope = Math.pow(1 - position, decayPower),
                ramp = frame < rampFrames ? frame / rampFrames : 1,
                spark = Math.random() < .3 + ramp * .7 ? 1 : 0,
                noise = (Math.random() * 2 - 1) * spark,
                smoothing = Math.max(.05, 1 - damping * position);
            filterState += (noise - filterState) * smoothing;
            data[frame] = filterState * envelope;
        }
    }
    return buffer;
}

const BUFFER_CACHE = new Map;

// A room tone made from nothing, for when the recording cannot be had.
//
// The bed above asks for /ambience/room-murmur.mp3 on the app's own origin. That file
// was never shipped, and the host answers a missing path with the web page itself and a
// 200, which decodeAudioData rejects - so the shop's room tone was silent, with no error
// to say so. This is what plays instead of silence: a low crowd-like murmur that swells
// and ebbs, a faint mains hum, and a little air. It loops seamlessly (the tail is
// cross-faded into the head) and is normalised so the bed's own gain means the same
// thing it would for a recording.
function makeRoomToneBuffer(context, seconds = 12) {
    const rate = context.sampleRate;
    const fade = Math.floor(rate * .6);
    const length = Math.floor(rate * seconds);
    const raw = new Float32Array(length + fade);
    let seed = 0x2545f491;
    const random = () => {
        seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
        return seed / 2147483648 - 1;
    };
    // murmur: white noise through two one-pole low-passes (about 330 Hz), then swelled by
    // two slow, unrelated envelopes so it never settles into a flat hiss
    let a = 0, b = 0, air = 0;
    const k = 1 - Math.exp(-2 * Math.PI * 330 / rate);
    for (let i = 0; i < raw.length; i++) {
        const t = i / rate;
        a += k * (random() - a);
        b += k * (a - b);
        air += .35 * (random() - air);
        const swell = .62 + .26 * Math.sin(2 * Math.PI * .11 * t + 1.3) + .12 * Math.sin(2 * Math.PI * .27 * t);
        const hum = .035 * Math.sin(2 * Math.PI * 60 * t) + .018 * Math.sin(2 * Math.PI * 120 * t);
        raw[i] = b * 7.5 * swell + hum + (random() - air) * .018;
    }
    const data = new Float32Array(length);
    for (let i = 0; i < length; i++) data[i] = raw[i];
    // fold the extra tail over the head so the join is continuous
    for (let i = 0; i < fade; i++) {
        const w = i / fade;
        data[i] = raw[i] * Math.sin(w * Math.PI / 2) + raw[length + i] * Math.cos(w * Math.PI / 2);
    }
    let sum = 0, peak = 0;
    for (let i = 0; i < length; i++) sum += data[i] * data[i];
    // Soft-limit the rare spikes first. Noise has a high crest factor, and a bed
    // normalised by its peak ends up several dB quieter than intended, because a few
    // outliers set the scale for everything else.
    const knee = 2.2 * Math.sqrt(sum / length);
    sum = 0;
    for (let i = 0; i < length; i++) {
        data[i] = Math.tanh(data[i] / knee) * knee;
        sum += data[i] * data[i];
        peak = Math.max(peak, Math.abs(data[i]));
    }
    const rms = Math.sqrt(sum / length) || 1;
    const scale = Math.min(.3 / rms, .9 / (peak || 1));
    for (let i = 0; i < length; i++) data[i] *= scale;
    const buffer = context.createBuffer(1, length, rate);
    buffer.copyToChannel(data, 0);
    return buffer;
}

function loadBuffer(context, url) {
    const key = `${url}@${context.sampleRate}`;
    let pending = BUFFER_CACHE.get(key);
    if (!pending) {
        pending = fetch(url).then(response => {
            if (!response.ok) throw new Error(String(response.status));
            return response.arrayBuffer();
        }).then(bytes => context.decodeAudioData(bytes));
        BUFFER_CACHE.set(key, pending);
        // Drops the cache entry on failure so a later call retries. The
        // returned promise is the original one, not this catch.
        pending.catch(() => BUFFER_CACHE.delete(key));
    }
    return pending;
}

function createShopAudio(camera, opts = {}) {
    const AudioContextCtor = typeof window < "u" ? window.AudioContext || window.webkitAudioContext : null,
        silentApi = {
            available: false,
            unlocked: false,
            speakers: SPEAKERS,
            ambience: AMBIENCE_BEDS,
            demoSeconds: DEMO_SECONDS,
            unlock: async () => false,
            update: () => {},
            playDemo: async () => false,
            stopDemo: () => {},
            isPlaying: () => false,
            demoTime: () => 0,
            demoRemaining: () => 0,
            setLevel: () => {},
            getLevel: () => 0,
            setAmbienceLevel: () => {},
            setMuted: () => {},
            level: () => 0,
            element: null,
            debug: () => ({
                available: false,
                reason: "no AudioContext"
            }),
            dispose: () => {}
        };
    if (!AudioContextCtor) return silentApi;
    let context;
    try {
        context = new AudioContextCtor({
            latencyHint: "interactive"
        });
    } catch {
        return silentApi;
    }
    if (!context || typeof context.createPanner != "function") return silentApi;

    const listener = context.listener,
        masterGain = context.createGain();
    masterGain.gain.value = 0;

    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -6;
    compressor.knee.value = 6;
    compressor.ratio.value = 8;
    compressor.attack.value = .004;
    compressor.release.value = .25;
    masterGain.connect(compressor);
    compressor.connect(context.destination);

    const reverbSend = context.createGain();
    reverbSend.gain.value = 1;

    const convolver = context.createConvolver();
    try {
        convolver.buffer = makeReverbImpulse(context);
    } catch {}

    const reverbWet = context.createGain();
    reverbWet.gain.value = .1;
    reverbSend.connect(convolver);
    convolver.connect(reverbWet);
    reverbWet.connect(masterGain);

    const programGain = context.createGain();
    programGain.gain.value = .85;

    const splitter = context.createChannelSplitter(2);
    programGain.connect(splitter);

    const speakerNodes = SPEAKERS.map(spec => {
            const lowpass = context.createBiquadFilter();
            lowpass.type = "lowpass";
            lowpass.frequency.value = 18e3;
            lowpass.Q.value = .5;

            const trim = context.createGain();
            trim.gain.value = spec.gain;

            const panner = context.createPanner();
            panner.panningModel = "HRTF";
            panner.distanceModel = "inverse";
            panner.refDistance = spec.refDistance;
            panner.rolloffFactor = spec.rolloff;
            panner.maxDistance = 24;
            panner.coneInnerAngle = 110;
            panner.coneOuterAngle = 290;
            panner.coneOuterGain = .45;
            panner.positionX.value = spec.x;
            panner.positionY.value = spec.y;
            panner.positionZ.value = spec.z;

            const aimX = spec.aim.x - spec.x,
                aimY = spec.aim.y - spec.y,
                aimZ = spec.aim.z - spec.z,
                aimLength = Math.hypot(aimX, aimY, aimZ) || 1;
            panner.orientationX.value = aimX / aimLength;
            panner.orientationY.value = aimY / aimLength;
            panner.orientationZ.value = aimZ / aimLength;

            splitter.connect(lowpass, spec.channel);
            lowpass.connect(trim);
            trim.connect(panner);
            panner.connect(masterGain);

            const send = context.createGain();
            send.gain.value = spec.role === "main" ? .16 : .1;
            panner.connect(send);
            send.connect(reverbSend);
            return {
                spec: spec,
                lowpass: lowpass,
                trim: trim,
                panner: panner,
                send: send
            };
        }),
        mainSpeakerNodes = speakerNodes.filter(node => node.spec.role === "main"),
        ambienceGain = context.createGain();
    ambienceGain.gain.value = opts.ambienceLevel ?? 1;
    ambienceGain.connect(masterGain);

    const ambienceVoices = [];
    let ambienceStarted = false;

    async function startAmbience() {
        if (ambienceStarted) return;
        ambienceStarted = true;
        for (const bed of AMBIENCE_BEDS) try {
            let buffer, synthesised = false;
            try {
                buffer = await loadBuffer(context, bed.url);
            } catch {
                // the recording is missing or undecodable: never leave the room silent
                if (bed.id !== "room-tone") continue;
                buffer = makeRoomToneBuffer(context);
                synthesised = true;
            }
            if (context.state === "closed") return;

            const source = context.createBufferSource();
            source.buffer = buffer;
            source.loop = true;
            source.playbackRate.value = bed.rate ?? 1;

            const gain = context.createGain();
            gain.gain.value = bed.gain;

            let tail = gain,
                eq = null;
            if (bed.eq) {
                eq = context.createBiquadFilter();
                eq.type = bed.eq.type;
                eq.frequency.value = bed.eq.freq;
                if (bed.eq.gain !== void 0) eq.gain.value = bed.eq.gain;
                if (bed.eq.q !== void 0) eq.Q.value = bed.eq.q;
                gain.connect(eq);
                tail = eq;
            }

            const panner = context.createPanner();
            panner.panningModel = "HRTF";
            panner.distanceModel = "inverse";
            panner.refDistance = bed.refDistance;
            panner.rolloffFactor = bed.rolloff;
            panner.maxDistance = 20;
            panner.coneInnerAngle = 360;
            panner.positionX.value = bed.x;
            panner.positionY.value = bed.y;
            panner.positionZ.value = bed.z;
            source.connect(gain);
            tail.connect(panner);
            panner.connect(ambienceGain);

            const send = context.createGain();
            send.gain.value = .12;
            panner.connect(send);
            send.connect(reverbSend);
            source.start(0, Math.random() * buffer.duration);
            ambienceVoices.push({
                spec: bed,
                synthesised: synthesised,
                src: source,
                gain: gain,
                panner: panner,
                eq: eq,
                send: send
            });
        } catch {}
    }

    const element = opts.element || (typeof Audio < "u" ? new Audio : null),
        adoptedElement = !!opts.element;
    let mediaSource = null;
    if (element) {
        element.crossOrigin = "anonymous";
        if (!adoptedElement) element.preload = "auto";
        element.loop = false;
        try {
            mediaSource = context.createMediaElementSource(element);
            mediaSource.connect(programGain);
        } catch {
            mediaSource = null;
        }
        try {
            element.addEventListener("play", () => {
                unlock();
            });
        } catch {}
    }

    let activeDemo = null,
        onCutCallback = opts.onCut || null;

    function finishDemo(reason) {
        if (!activeDemo) return;
        const demo = activeDemo;
        activeDemo = null;
        if (demo.timer) clearTimeout(demo.timer);
        try {
            element.pause();
        } catch {}
        if (demo.onEnd) try {
            demo.onEnd(reason);
        } catch {}
        if (reason === "cut" && onCutCallback) try {
            onCutCallback(demo.url);
        } catch {}
    }

    function fadeOutDemo(reason) {
        if (!activeDemo) return;
        const now = context.currentTime;
        try {
            programGain.gain.cancelScheduledValues(now);
            programGain.gain.setValueAtTime(programGain.gain.value, now);
            programGain.gain.linearRampToValueAtTime(1e-4, now + .25);
        } catch {}
        const demo = activeDemo;
        demo.timer = setTimeout(() => {
            finishDemo(reason);
            try {
                const then = context.currentTime;
                programGain.gain.cancelScheduledValues(then);
                programGain.gain.setValueAtTime(.85, then);
            } catch {}
        }, 280);
    }

    async function playDemo(url, options = {}) {
        if (!element) return false;
        stopDemo();
        const limit = options.maxSeconds ?? DEMO_SECONDS;
        activeDemo = {
            url: url,
            started: 0,
            limit: limit,
            onEnd: options.onEnd || null,
            timer: null
        };
        try {
            const now = context.currentTime;
            programGain.gain.cancelScheduledValues(now);
            programGain.gain.setValueAtTime(.85, now);
        } catch {}
        try {
            element.src = url;
            element.currentTime = 0;
            element.onended = () => fadeOutDemo("ended");
            element.onerror = () => finishDemo("error");
            await element.play();
            if (activeDemo) {
                activeDemo.started = context.currentTime;
                return true;
            }
            return false;
        } catch {
            finishDemo("error");
            return false;
        }
    }

    function stopDemo() {
        if (!activeDemo) return;
        if (activeDemo.timer) clearTimeout(activeDemo.timer);
        const demo = activeDemo;
        activeDemo = null;
        try {
            element.pause();
            element.onended = null;
            element.onerror = null;
        } catch {}
        try {
            const now = context.currentTime;
            programGain.gain.cancelScheduledValues(now);
            programGain.gain.setValueAtTime(.85, now);
        } catch {}
        if (demo.onEnd) try {
            demo.onEnd("stopped");
        } catch {}
    }

    const forward = {
            x: 0,
            y: 0,
            z: -1
        },
        up = {
            x: 0,
            y: 1,
            z: 0
        },
        matrixElements = new Float32Array(16);

    // Copies the camera's world matrix onto the AudioListener. Returns the
    // listener position so update() can measure distances from it.
    function syncListener() {
        if (!camera) return;
        camera.updateMatrixWorld?.();
        const elements = camera.matrixWorld?.elements;
        if (!elements) return;
        for (let i = 0; i < 16; i += 1) matrixElements[i] = elements[i];
        forward.x = -matrixElements[8];
        forward.y = -matrixElements[9];
        forward.z = -matrixElements[10];
        up.x = matrixElements[4];
        up.y = matrixElements[5];
        up.z = matrixElements[6];
        const px = matrixElements[12],
            py = matrixElements[13],
            pz = matrixElements[14],
            now = context.currentTime;
        if (listener.positionX) {
            listener.positionX.setTargetAtTime(px, now, .02);
            listener.positionY.setTargetAtTime(py, now, .02);
            listener.positionZ.setTargetAtTime(pz, now, .02);
            listener.forwardX.setTargetAtTime(forward.x, now, .02);
            listener.forwardY.setTargetAtTime(forward.y, now, .02);
            listener.forwardZ.setTargetAtTime(forward.z, now, .02);
            listener.upX.setTargetAtTime(up.x, now, .02);
            listener.upY.setTargetAtTime(up.y, now, .02);
            listener.upZ.setTargetAtTime(up.z, now, .02);
        } else if (listener.setPosition) {
            listener.setPosition(px, py, pz);
            listener.setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z);
        }
        return {
            px: px,
            py: py,
            pz: pz
        };
    }

    let nearestLowpassHz = 18e3;

    function update() {
        const pos = syncListener();
        if (!pos) return;
        const now = context.currentTime;
        let nearestSpeaker = 1 / 0;
        for (const node of speakerNodes) {
            const distance = Math.hypot(pos.px - node.spec.x, pos.py - node.spec.y, pos.pz - node.spec.z);
            if (distance < nearestSpeaker) nearestSpeaker = distance;
            const cutoff = 18e3 - clamp((distance - 1.6) / 9, 0, 1) * 14500;
            node.lowpass.frequency.setTargetAtTime(cutoff, now, .08);
        }
        let nearestMain = 1 / 0;
        for (const node of mainSpeakerNodes) {
            const distance = Math.hypot(pos.px - node.spec.x, pos.pz - node.spec.z);
            if (distance < nearestMain) nearestMain = distance;
        }
        const wetAmount = clamp((nearestMain - 1.5) / 9, 0, 1);
        reverbWet.gain.setTargetAtTime(.08 + wetAmount * .26, now, .12);
        nearestLowpassHz = 18e3 - clamp((nearestSpeaker - 1.6) / 9, 0, 1) * 14500;
        if (activeDemo && element && element.currentTime >= activeDemo.limit) fadeOutDemo("cut");
    }

    let unlocked = false,
        level = opts.level ?? .8;

    async function unlock() {
        try {
            if (context.state === "suspended") await context.resume();
        } catch {}
        if (context.state !== "running") return false;
        if (!unlocked) {
            unlocked = true;
            const now = context.currentTime;
            masterGain.gain.cancelScheduledValues(now);
            masterGain.gain.setValueAtTime(1e-4, now);
            masterGain.gain.linearRampToValueAtTime(level, now + .6);
            startAmbience();
        }
        return true;
    }

    function setLevel(value, timeConstant = .15) {
        level = clamp(value, 0, 1);
        if (!unlocked) return;
        try {
            masterGain.gain.setTargetAtTime(level, context.currentTime, timeConstant);
        } catch {}
    }

    function setAmbienceLevel(value, timeConstant = .4) {
        try {
            ambienceGain.gain.setTargetAtTime(clamp(value, 0, 1), context.currentTime, timeConstant);
        } catch {}
    }

    return {
        available: true,
        speakers: SPEAKERS,
        ambience: AMBIENCE_BEDS,
        demoSeconds: DEMO_SECONDS,
        context: context,
        // the master bus: foley joins it so it shares the compressor and the mute
        output: masterGain,
        element: element,
        adopted: adoptedElement,
        routed: () => !!mediaSource,
        panners: speakerNodes.map(node => node.panner),
        unlock: unlock,
        update: update,
        playDemo: playDemo,
        stopDemo: stopDemo,
        isPlaying: () => !!activeDemo && !!element && !element.paused,
        demoTime: () => element && element.currentTime || 0,
        demoRemaining: () => activeDemo && element ? Math.max(0, activeDemo.limit - (element.currentTime || 0)) : 0,
        setLevel: setLevel,
        getLevel: () => level,
        setAmbienceLevel: setAmbienceLevel,
        setMuted: muted => setLevel(muted ? 0 : opts.level ?? .8),
        onCut: callback => {
            onCutCallback = callback;
        },
        get unlocked() {
            return unlocked;
        },
        debug: () => ({
            available: true,
            state: context.state,
            unlocked: unlocked,
            masterGain: masterGain.gain.value,
            speakers: speakerNodes.length,
            hrtf: speakerNodes.every(node => node.panner.panningModel === "HRTF"),
            adoptedElement: adoptedElement,
            routed: !!mediaSource,
            ambienceVoices: ambienceVoices.length,
            ambienceStarted: ambienceStarted,
            ambienceSynthesised: ambienceVoices.filter(voice => voice.synthesised).map(voice => voice.spec.id),
            wet: reverbWet.gain.value,
            lowpassHz: Math.round(nearestLowpassHz),
            playing: !!activeDemo && !!element && !element.paused,
            demoTime: element && element.currentTime || 0,
            demoLimit: activeDemo ? activeDemo.limit : DEMO_SECONDS,
            listener: {
                x: listener.positionX ? listener.positionX.value : null,
                z: listener.positionZ ? listener.positionZ.value : null,
                fz: listener.forwardZ ? listener.forwardZ.value : null
            }
        }),
        dispose: () => {
            stopDemo();
            for (const voice of ambienceVoices) {
                try {
                    voice.src.stop();
                } catch {}
                for (const node of [voice.src, voice.gain, voice.eq, voice.panner, voice.send]) try {
                    node?.disconnect();
                } catch {}
            }
            ambienceVoices.length = 0;
            try {
                context.close();
            } catch {}
        }
    };
}

export { clamp, SPEAKERS, AMBIENCE_BEDS, DEMO_SECONDS, createShopAudio, makeRoomToneBuffer };
